import { useState } from "react";
import {
  format,
  subMonths,
  addMonths,
  startOfWeek,
  addDays,
  isSameDay,
  lastDayOfWeek,
  getWeek,
  addWeeks,
  subWeeks,
} from "date-fns";
import "./styles.css";
import ClockIcon from "@mui/icons-material/Timer";
import { Project, ProjectStatus } from "../../../types/project";
import {
  Box,
  Typography,
  IconButton,
  styled,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useUILabel } from "../../../hooks/useUILabel";

const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.03)"
    : "rgba(0, 0, 0, 0.01)",
  flexWrap: "nowrap",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 2.25),
    gap: theme.spacing(1.25),
  },
}));

const StyledNavButton = styled(IconButton)(({ theme }) => ({
  borderRadius: 10,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  flexShrink: 0,
  width: 36,
  height: 36,
  "&:hover": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : "rgba(0, 0, 0, 0.05)",
    transform: "scale(1.1)",
  },
}));

const StyledHeaderTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  flexGrow: 1,
  minWidth: 0,
  flexShrink: 1,
  gap: theme.spacing(0.25),
}));

const StyledWeekTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 700,
  color: theme.palette.text.primary,
  letterSpacing: "0.5px",
  textTransform: "none",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.2rem",
  },
}));

const StyledWeekSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.95rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  lineHeight: 1.25,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
  },
}));

const StyledDaysHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"}`,
  backgroundColor: theme.palette.mode === "dark"
    ? "rgba(255, 255, 255, 0.02)"
    : "rgba(0, 0, 0, 0.01)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75, 0),
  },
}));

const StyledDayHeader = styled(Box)(({ theme }) => ({
  flex: 1,
  textAlign: "center",
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  padding: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.65rem",
    padding: theme.spacing(0.5),
    letterSpacing: "0.2px",
  },
}));

const StyledCalendarBody = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: "60vh",
}));

const StyledCalendarRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
  "&:last-child": {
    borderBottom: "none",
  },
}));

const StyledCalendarCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isToday" && prop !== "isSelected",
})<{ isToday?: boolean; isSelected?: boolean; }>(
  ({ theme, isToday, isSelected }) => ({
    flex: 1,
    minHeight: "150px",
    padding: theme.spacing(1),
    borderRight: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    position: "relative",
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.01)"
      : "rgba(0, 0, 0, 0.005)",
    overflow: "hidden",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      minHeight: "100px",
      padding: theme.spacing(0.5),
    },
    "&:last-child": {
      borderRight: "none",
    },
    "&:hover": {
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
    },
    ...(isToday && {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(25, 118, 210, 0.1)"
        : "rgba(25, 118, 210, 0.05)",
      [theme.breakpoints.down("sm")]: {
        borderLeft: `2px solid ${theme.palette.primary.main}`,
      },
    }),
    ...(isSelected && {
      borderLeft: `4px solid ${theme.palette.secondary.main}`,
      backgroundColor: theme.palette.mode === "dark"
        ? "rgba(156, 39, 176, 0.1)"
        : "rgba(156, 39, 176, 0.05)",
      [theme.breakpoints.down("sm")]: {
        borderLeft: `2px solid ${theme.palette.secondary.main}`,
      },
    }),
  })
);

const StyledDayNumber = styled(Typography)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  fontSize: "0.875rem",
  fontWeight: 600,
  color: theme.palette.text.primary,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.7rem",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
  },
}));

const StyledJobCard = styled(Box)<{ bgColor: string; }>(
  ({ theme, bgColor }) => ({
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    cursor: "pointer",
    padding: theme.spacing(0.75, 1),
    margin: theme.spacing(0.5, 0),
    borderRadius: 8,
    backgroundColor: bgColor,
    color: "white",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: theme.palette.mode === "dark"
      ? "0 2px 8px rgba(0, 0, 0, 0.3)"
      : "0 2px 8px rgba(0, 0, 0, 0.15)",
    overflow: "hidden",
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.4, 0.6),
      margin: theme.spacing(0.2, 0),
      borderRadius: 4,
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.palette.mode === "dark"
        ? "0 4px 12px rgba(0, 0, 0, 0.4)"
        : "0 4px 12px rgba(0, 0, 0, 0.2)",
    },
  })
);

const StyledJobTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.8rem",
  fontWeight: 600,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  marginTop: 4,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  display: "block",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.6rem",
    marginTop: 2,
    lineHeight: 1.2,
  },
}));

const StyledJobTime = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontSize: "0.75rem",
  fontWeight: 500,
  marginBottom: 4,
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.55rem",
    gap: 2,
    marginBottom: 2,
  },
}));

const WeeklyCalendar = ({ data }: { data: Project[] | undefined; }) => {
  const locationLabel = useUILabel("common.location");
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentWeek, setCurrentWeek] = useState(getWeek(currentMonth));
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedCellDate, setSelectedCellDate] = useState<Date | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const weekStartIndex = 0;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const changeMonthHandle = (btnType: "prev" | "next") => {
    if (btnType === "prev") {
      setCurrentMonth(subMonths(currentMonth, 1));
    }
    if (btnType === "next") {
      setCurrentMonth(addMonths(currentMonth, 1));
    }
  };

  const changeWeekHandle = (btnType: "prev" | "next") => {
    //console.log("current week", currentWeek);
    if (btnType === "prev") {
      //console.log(subWeeks(currentMonth, 1));
      setCurrentMonth(subWeeks(currentMonth, 1));
      setCurrentWeek(getWeek(subWeeks(currentMonth, 1)));
    }
    if (btnType === "next") {
      //console.log(addWeeks(currentMonth, 1));
      setCurrentMonth(addWeeks(currentMonth, 1));
      setCurrentWeek(getWeek(addWeeks(currentMonth, 1)));
    }
    setSelectedCellDate(null);
    setSelectedDate(null);
  };

  const onDateClickHandle = (day: any) => {
    console.log("onDateClickHandle called:", { day, selectedCellDate });
    if (selectedCellDate && isSameDay(day, selectedCellDate)) {
      console.log("Deselecting cell");
      setSelectedCellDate(null);
      setSelectedDate(null);
    } else {
      // Create a new Date object with the same date but ensure it's at midnight local time
      const newDate = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      console.log("Selecting cell:", newDate, "original day:", day);
      setSelectedCellDate(newDate);
      setSelectedDate(newDate);
    }
  };

  const isCellSelected = (day: Date): boolean => {
    if (!selectedCellDate) {
      return false;
    }
    // Normalize both dates to midnight local time for accurate comparison
    const dayNormalized = new Date(day.getFullYear(), day.getMonth(), day.getDate());
    const selectedNormalized = new Date(selectedCellDate.getFullYear(), selectedCellDate.getMonth(), selectedCellDate.getDate());
    const result = dayNormalized.getTime() === selectedNormalized.getTime();
    // Only log when checking the selected day to reduce noise
    if (result || (day.getDate() === selectedCellDate.getDate() &&
      day.getMonth() === selectedCellDate.getMonth() &&
      day.getFullYear() === selectedCellDate.getFullYear())) {
      console.log("isCellSelected:", {
        day: day.toISOString(),
        selectedCellDate: selectedCellDate.toISOString(),
        dayNormalized: dayNormalized.toISOString(),
        selectedNormalized: selectedNormalized.toISOString(),
        result,
        dayDate: day.getDate(),
        selectedDate: selectedCellDate.getDate(),
        dayMonth: day.getMonth(),
        selectedMonth: selectedCellDate.getMonth(),
        dayYear: day.getFullYear(),
        selectedYear: selectedCellDate.getFullYear()
      });
    }
    return result;
  };

  const renderHeader = () => {
    const monthLabel = format(currentMonth, "MMM yyyy");
    return (
      <StyledHeader>
        <StyledNavButton onClick={() => changeWeekHandle("prev")} size="small">
          <ChevronLeftIcon />
        </StyledNavButton>
        {isMobile ? (
          <StyledHeaderTitle>
            <StyledWeekTitle>Week {currentWeek}</StyledWeekTitle>
            <StyledWeekSubtitle>{monthLabel}</StyledWeekSubtitle>
          </StyledHeaderTitle>
        ) : (
          <StyledWeekTitle>
            Week {currentWeek} - {monthLabel}
          </StyledWeekTitle>
        )}
        <StyledNavButton onClick={() => changeWeekHandle("next")} size="small">
          <ChevronRightIcon />
        </StyledNavButton>
      </StyledHeader>
    );
  };
  const renderDays = () => {
    const dateFormat = "EEE";
    const days = [];
    let startDate = startOfWeek(currentMonth, { weekStartsOn: weekStartIndex });
    for (let i = 0; i < 7; i++) {
      days.push(
        <StyledDayHeader key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </StyledDayHeader>
      );
    }
    return <StyledDaysHeader>{days}</StyledDaysHeader>;
  };
  const getTodaysjobs = (todaysDate: Date): Project[] => {
    const list = data?.filter((project: Project) => {
      const startDate = dayjs(project.start_date);
      const projectDate = startDate.get("date");
      const projectYear = startDate.get("year");
      const projectMonth = startDate.get("month");
      return (
        (project.status === ProjectStatus.SCHEDULED ||
          project.status === ProjectStatus.IN_PROGRESS ||
          project.status === ProjectStatus.PUBLISHED) &&
        todaysDate.getDate() == projectDate &&
        todaysDate.getMonth() == projectMonth &&
        todaysDate.getFullYear() == projectYear
      );
    });
    return list || [];
  };
  function renderJobs(day: Date) {
    const jobs = getTodaysjobs(day);

    // Limit jobs on mobile to prevent layout issues
    const maxJobsMobile = 2;
    const jobsToShow = isMobile ? jobs.slice(0, maxJobsMobile) : jobs;
    const remainingJobs = isMobile ? jobs.length - maxJobsMobile : 0;

        return (
          <Box
            sx={{
              display: "flex",
          flexDirection: "column",
          gap: 0.2,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          maxHeight: { xs: "45px", sm: "none" },
          overflowY: { xs: "auto", sm: "visible" },
          overflowX: "hidden",
          boxSizing: "border-box",
          "&::-webkit-scrollbar": {
            width: "2px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 255, 255, 0.3)",
            borderRadius: "2px",
          },
                }}
              >
        {jobsToShow.map((project) => {
      let bgColor = "#0288D1"; // default color
      if (project.status === ProjectStatus.PUBLISHED) {
        bgColor = "#0288D1";
      } else if (project.status === ProjectStatus.SCHEDULED) {
        bgColor = "#ED6C02";
      } else if (project.status === ProjectStatus.IN_PROGRESS) {
        bgColor = "#ED6C02";
      }
      return (
        <StyledJobCard
          key={project.id}
          bgColor={bgColor as string}
              onClick={(e) => {
                e.stopPropagation();
                if (isMobile) {
                  setSelectedProject(project);
                  setOpenModal(true);
                } else {
                  navigate(`/works/${project?.id}`);
                }
              }}
        >
          <StyledJobTime>
                <ClockIcon sx={{ fontSize: { xs: "0.55rem", sm: "0.875rem" }, flexShrink: 0 }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "0.55rem", sm: "0.75rem" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
              {dayjs(project.start_date).format("hh:mm A z")}
            </Typography>
          </StyledJobTime>
          <StyledJobTitle>{project.title}</StyledJobTitle>
        </StyledJobCard>
      );
        })}
        {remainingJobs > 0 && (
          <Box
            sx={{
              mt: 0.2,
              px: 0.5,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              fontSize: { xs: "0.5rem", sm: "0.65rem" },
              fontWeight: 600,
              textAlign: "center",
              color: "white",
            }}
          >
            +{remainingJobs} more
          </Box>
        )}
      </Box>
    );
  }
  const renderCells = () => {
    const startDate = startOfWeek(currentMonth, {
      weekStartsOn: weekStartIndex,
    });
    const endDate = lastDayOfWeek(currentMonth, {
      weekStartsOn: weekStartIndex,
    });
    const dateFormat = "d";
    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";
    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        const isToday = isSameDay(day, new Date());
        const isSelected: boolean = isCellSelected(day);
        const hasJobs = getTodaysjobs(day).length > 0;
        days.push(
          <StyledCalendarCell
            key={day.toString()}
            isToday={isToday}
            isSelected={isSelected}
            onClick={(e) => {
              e.stopPropagation();
              // Handle click for both mobile and desktop
              if (!isMobile && hasJobs) {
                onDateClickHandle(day);
              }
            }}
            sx={{
              cursor: !isMobile && hasJobs ? "pointer" : "default",
            }}
          >
            <StyledDayNumber>{formattedDate}</StyledDayNumber>
            <Box sx={{ height: { xs: 16, sm: 40 }, pointerEvents: "none" }} />
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
            {renderJobs(day)}
            </Box>
          </StyledCalendarCell>
        );
        day = addDays(day, 1);
      }

      rows.push(
        <StyledCalendarRow key={day.toString()}>
          {days}
        </StyledCalendarRow>
      );
      days = [];
    }
    return <StyledCalendarBody>{rows}</StyledCalendarBody>;
  };
  const renderFooter = () => {
    return (
      <div className="header row flex-middle">
        <div className="col col-start">
          <div className="icon" onClick={() => changeWeekHandle("prev")}>
            prev week
          </div>
        </div>
        <div>{currentWeek}</div>
        <div className="col col-end" onClick={() => changeWeekHandle("next")}>
          <div className="icon">next week</div>
        </div>
      </div>
    );
  };
  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PUBLISHED:
        return "#0288D1";
      case ProjectStatus.SCHEDULED:
        return "#ED6C02";
      case ProjectStatus.IN_PROGRESS:
        return "#FF0000";
      default:
        return "#757575";
    }
  };

  const getStatusLabel = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PUBLISHED:
        return "Published";
      case ProjectStatus.SCHEDULED:
        return "Scheduled";
      case ProjectStatus.IN_PROGRESS:
        return "In Progress";
      default:
        return status;
    }
  };

  return (
    <Box>
      {renderHeader()}
      <Box>
        {renderDays()}
        {renderCells()}
      </Box>

      {/* Job Detail Modal - Mobile Only */}
      {isMobile && (
        <Dialog
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setSelectedProject(null);
          }}
          maxWidth="md"
          fullWidth
          fullScreen
          PaperProps={{
            sx: {
              borderRadius: 0,
              maxHeight: "100%",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Job Details
            </Typography>
            <IconButton
              onClick={() => {
                setOpenModal(false);
                setSelectedProject(null);
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2 }}>
            {selectedProject && (
              <Stack spacing={2.5}>
                {/* Title and Status */}
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: { xs: "1.25rem", sm: "1.5rem" },
                    }}
                  >
                    {selectedProject.title}
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedProject.status)}
                    sx={{
                      backgroundColor: getStatusColor(selectedProject.status),
                      color: "white",
                      fontWeight: 600,
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    }}
                  />
                </Box>

                {/* Date and Time */}
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <ClockIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      Schedule
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    {dayjs(selectedProject.start_date).format("dddd, MMMM DD, YYYY")}
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    {dayjs(selectedProject.start_date).format("hh:mm A z")} - {dayjs(selectedProject.end_date).format("hh:mm A z")}
                  </Typography>
                </Box>

                {/* Location */}
                {selectedProject.location && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <LocationOnIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {locationLabel}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {selectedProject.location.name || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                      {selectedProject.location.address_1}, {selectedProject.location.city}, {selectedProject.location.state} {selectedProject.location.zip}
                    </Typography>
                  </Box>
                )}

                {/* Company */}
                {selectedProject.company && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <BusinessIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        Company
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {selectedProject.company.name || "N/A"}
                    </Typography>
                  </Box>
                )}

                {/* Manager */}
                {selectedProject.manager && (
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <PersonIcon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, color: "primary.main" }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        Manager
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ ml: 3.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {selectedProject.manager.first_name} {selectedProject.manager.last_name}
                    </Typography>
                  </Box>
                )}

                {/* Description */}
                {selectedProject.description && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      Description
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedProject.description}
                    </Typography>
                  </Box>
                )}

                {/* Additional Info */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                    Additional Information
                  </Typography>
                  <Stack spacing={1}>
                    {selectedProject.type && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Type:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          {selectedProject.type === "hourly" ? "Hourly" : "Fixed"}
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.hourly_rate && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Hourly Rate:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          ${selectedProject.hourly_rate.toLocaleString()}/hr
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.total_pay && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Total Pay:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          ${selectedProject.total_pay.toLocaleString()}
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.approximate_hours_to_complete && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Approximate Hours:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          {selectedProject.approximate_hours_to_complete} hours
                        </Typography>
                      </Box>
                    )}
                    {selectedProject.num_of_talent && (
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, color: "text.secondary" }}>
                          Number of Talent:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500 }}>
                          {selectedProject.num_of_talent}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Stack>
            )}
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Button
              onClick={() => {
                setOpenModal(false);
                setSelectedProject(null);
              }}
              sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
            >
              Close
            </Button>
            {selectedProject && (
              <Button
                variant="contained"
                onClick={() => {
                  navigate(`/works/${selectedProject.id}`);
                  setOpenModal(false);
                  setSelectedProject(null);
                }}
                endIcon={<ArrowForwardIcon />}
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                Go To Detail
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default WeeklyCalendar;
/**
 * Header:
 * icon for switching to the previous month,
 * formatted date showing current month and year,
 * another icon for switching to next month
 * icons should also handle onClick events to change a month
 */
